'use client';

import type { ClassValue } from 'clsx';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from '@vs-shop/ui/dialog';
import { useMemo, useState } from 'react';
import { useIntl } from 'react-intl';
import { MoonLoader } from 'react-spinners';

import ProductPriceHistoryModalChart from './ProductPriceHistoryModalChart';
import ProductPriceHistoryTrigger from './ProductPriceHistoryTrigger';

import { fetchProductPriceHistoryAtom } from '../../../../../effect/atom/product/productPriceHistory';
import * as AsyncResult from 'effect/unstable/reactivity/AsyncResult';
import { useAtomValue } from '@effect/atom-react';

type Props = {
  productCode: string;
  productName: string;
  className?: ClassValue;
};

export default function ProductPriceHistoryInteractive({
  productCode,
  productName,
  className,
}: Props) {
  const intl = useIntl();
  const [isOpen, setIsOpen] = useState(true);

  const priceHistoryResult = useAtomValue(fetchProductPriceHistoryAtom(productCode));
  const isLoading = priceHistoryResult.waiting;

  const data = useMemo(() => {
    return AsyncResult.match(priceHistoryResult, {
      onInitial: () => [],
      onFailure: () => [],
      onSuccess: (success) => success.value,
    });
  }, [priceHistoryResult]);

  return (
    <Dialog open={isOpen} onOpenChange={setIsOpen}>
      <ProductPriceHistoryTrigger className={className} />

      {isOpen && (
        <DialogContent className="tab:max-w-[min(var(--container-4xl),calc(100%-2rem))]">
          <DialogHeader>
            <DialogTitle>
              {intl.formatMessage({
                id: 'productPriceHistoryModal.heading',
                defaultMessage: 'Historie cen',
              })}
            </DialogTitle>
            <DialogDescription>{productName}</DialogDescription>
          </DialogHeader>

          {isLoading ? (
            <div className="flex items-center justify-center py-4 text-foreground-muted">
              <MoonLoader />
            </div>
          ) : (
            <ProductPriceHistoryModalChart data={data} />
          )}
        </DialogContent>
      )}
    </Dialog>
  );
}
